import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { LoadingSpinner } from "../LoadingSpinner";
import "./Button.css";

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  variant?:
    | "primary"
    | "secondary"
    | "secondary-danger"
    | "danger"
    | "warning"
    | "success"
    | "ghost"
    | "link"
    | "link-primary"
    | "sky";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  /**
   * Optional tag to display on the right side of the button (like keyboard shortcut, counter, or custom component)
   */
  tag?: ReactNode | string;
  children?: ReactNode;
  /**
   * When true, collapses the button to icon-only until hover/focus
   */
  collapseToIcon?: boolean;
  /**
   * Loops an animation on the icon to show background work in progress.
   * Unlike `loading`, the button stays enabled, so it can still open the
   * work's status. Ignored while `loading`.
   */
  iconAnimation?: "pulse";
}

/**
 * buttonVariants - CVA configuration for Button component variants and sizes
 * Uses CSS custom properties that map to our Figma design tokens
 */
const buttonVariants = cva("indigo-button-base", {
  variants: {
    variant: {
      primary: "indigo-button-primary",
      secondary: "indigo-button-secondary",
      "secondary-danger": "indigo-button-secondary-danger",
      danger: "indigo-button-danger",
      warning: "indigo-button-warning",
      success: "indigo-button-success",
      ghost: "indigo-button-ghost",
      link: "indigo-button-link",
      "link-primary": "indigo-button-link-primary",
      sky: "indigo-button-sky",
    },
    size: {
      xs: "indigo-button-xs",
      sm: "indigo-button-sm",
      md: "indigo-button-md",
      lg: "indigo-button-lg",
      xl: "indigo-button-xl",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "md",
  },
});

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "lg",
      loading = false,
      icon,
      iconPosition = "left",
      tag,
      children,
      className,
      disabled,
      collapseToIcon = false,
      iconAnimation,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    const iconElement = loading ? <LoadingSpinner size={20} /> : icon;
    const shouldCollapse = collapseToIcon && Boolean(iconElement);
    const iconAnimationClass =
      iconAnimation && !loading
        ? `indigo-button-icon-${iconAnimation}`
        : undefined;

    const buttonClass = buttonVariants({ variant, size });
    const combinedClassName = clsx(
      buttonClass,
      // Only `xl` reaches the 44px touch minimum on its own; the rest keep
      // their painted height and grow the hit area on coarse pointers. The
      // `max(100%, …)` in the rule makes this a no-op for `xl`, so it's
      // applied unconditionally rather than branching on size.
      "indigo-touch-target",
      className,
      shouldCollapse && "indigo-button-collapsible",
    );

    const { ["aria-label"]: ariaLabelProp, ...restProps } = props;
    const computedAriaLabel =
      ariaLabelProp ||
      (shouldCollapse && typeof children === "string" ? children : undefined);

    return (
      <button
        ref={ref}
        className={combinedClassName}
        disabled={isDisabled}
        aria-label={computedAriaLabel}
        {...restProps}
      >
        {iconElement && iconPosition === "left" && (
          <span
            className={clsx(
              children ? "indigo-button-icon-left" : "indigo-button-icon-only",
              iconAnimationClass,
            )}
          >
            {iconElement}
          </span>
        )}
        {children && shouldCollapse ? (
          <span className="indigo-button-label">{children}</span>
        ) : (
          children
        )}
        {tag &&
          (typeof tag === "string" ? (
            <span className="indigo-button-tag">{tag}</span>
          ) : (
            <div className="ml-2 -mt-1">{tag}</div>
          ))}
        {iconElement && iconPosition === "right" && (
          <span
            className={clsx(
              children ? "indigo-button-icon-right" : "indigo-button-icon-only",
              iconAnimationClass,
            )}
          >
            {iconElement}
          </span>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export { buttonVariants };
